import { useCallback, useEffect, useMemo, useState } from 'react';
import '../../backlog/backlog.css';
import './dailyQcBuilder.css';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { TopbarStepper } from '../../../components/StepIsland/TopbarStepper';
import type { IslandStep } from '../../../components/StepIsland/StepIsland';
import { useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { formatCombo, useHotkeys } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { confirmDiscardIfUnsaved, resetUnsavedTracking, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import {
  FORM_NAME, MAX_INSTRUCTIONS, MAX_TEMPLATE_NAME, NO_RECURRENCE, createTemplate, deleteTemplate, emptyDraft, errorText,
  loadAvailable, loadInstruments, loadMethods, loadOptions, loadTemplate, updateTemplate,
  type AvailableField, type BuilderDraft, type BuilderField, type BuilderTemplate, type InstrumentOption, type Option,
} from './builderApi';
import BuilderFieldConfig from './BuilderFieldConfig';
import BuilderFormulaDialog from './BuilderFormulaDialog';
import BuilderRecurrenceDialog from './BuilderRecurrenceDialog';
import BuilderPreview from './BuilderPreview';

/** Sentinels for a demo template's instrument / method text that is not a real row. */
const CODE_ONLY = '__code';
const TEXT_ONLY = '__text';

interface Props {
  /** null = New. */
  id: string | null;
  title: string;
  group?: string;
  onHome?: () => void;
  /** Back to the template list. */
  onClose: () => void;
  /** After a save / delete — the list refreshes. */
  onSaved: () => void;
}

const toDraft = (t: BuilderTemplate): BuilderDraft => ({
  templateName: t.templateName, categoryOid: t.categoryOid, testOid: t.testOid, methodOid: t.methodOid,
  methodText: t.methodText, instrumentOid: t.instrumentOid, instrumentCode: t.instrumentCode,
  instrumentName: t.instrumentName, startDate: t.startDate, recurrence: t.recurrence,
  recurrenceSummary: t.recurrenceSummary, status: t.status, notifyOnFailure: t.notifyOnFailure,
  instructions: t.instructions, fields: t.fields,
});
const same = (a: BuilderDraft, b: BuilderDraft) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Settings ▸ Analysis ▸ Daily QC Log Book Builder — the detail page, built exactly as the
 * owner's prototype ALPACA_Daily_QC_Setup_v20.html (owner decision 2026-09-28: keep the list;
 * New / Edit open THIS page). Two tabs — the Builder (header + Field Configuration, with the
 * Formula Editor and Recurrence Setup popups) and the Logbook Preview. ONE draft, ONE Save:
 * nothing is written until Save / Save Configuration, which the API validates and writes in
 * one transaction (DailyQcBuilderController).
 */
export default function DailyQcBuilderPage({ id, title, group, onHome, onClose, onSaved }: Props) {
  const notify = useNotify();
  const { prompt, confirm } = useConfirm();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [currentId, setCurrentId] = useState<string | null>(id);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [saved, setSaved] = useState<BuilderDraft>(emptyDraft);
  const [draft, setDraft] = useState<BuilderDraft>(emptyDraft);
  const [tab, setTab] = useState<'builder' | 'preview'>('builder');
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [categories, setCategories] = useState<Option[]>([]);
  const [tests, setTests] = useState<Option[]>([]);
  const [methods, setMethods] = useState<Option[]>([]);
  const [instruments, setInstruments] = useState<InstrumentOption[]>([]);
  const [available, setAvailable] = useState<AvailableField[]>([]);
  const [formula, setFormula] = useState<{ index: number; kind: 'calc' | 'accept' } | null>(null);
  const [recurrenceOpen, setRecurrenceOpen] = useState(false);

  const dirty = !same(draft, saved);
  useUnsavedGuard(dirty);

  // Lookups — once.
  useEffect(() => {
    Promise.all([loadOptions('categories'), loadOptions('tests'), loadAvailable()])
      .then(([c, t, a]) => { setCategories(c); setTests(t); setAvailable(a); })
      .catch((e) => notify.error(errorText(e, 'Failed to load the lists.')));
  }, [notify]);

  const load = useCallback(async (oid: string | null) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      if (oid) {
        const t = await loadTemplate(oid);
        const d = toDraft(t);
        setTemplateId(t.templateId); setSaved(d); setDraft(d);
      } else {
        const d = emptyDraft();
        setTemplateId(null); setSaved(d); setDraft(d);
      }
      setCurrentId(oid);
      resetUnsavedTracking();
    } catch (e) {
      notify.error(errorText(e, 'Failed to load the template.'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(id); }, [id, load]);

  // Method cascades off QC Check (the Test).
  useEffect(() => {
    loadMethods(draft.testOid).then(setMethods).catch((e) => notify.error(errorText(e, 'Failed to load the methods.')));
  }, [draft.testOid, notify]);

  // Instrument ID cascades off QC Check + Method, as G5 filters it (only that TestMethod's
  // instruments; none until both are chosen). The current instrument is always kept.
  useEffect(() => {
    loadInstruments(draft.testOid, draft.methodOid, draft.instrumentOid).then(setInstruments)
      .catch((e) => notify.error(errorText(e, 'Failed to load the instruments.')));
  }, [draft.testOid, draft.methodOid, draft.instrumentOid, notify]);

  const set = (patch: Partial<BuilderDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const setFields = (fields: BuilderField[]) => set({ fields });

  // ── actions ──────────────────────────────────────────────────────────────

  const save = useCallback(async () => {
    const body = draft;
    let createdId: string | null = null;
    await runSave({
      formName: FORM_NAME,
      auditAction: currentId ? 'edit' : 'create',
      save: async () => {
        if (currentId) await updateTemplate(currentId, body);
        else createdId = (await createTemplate(body)).oid;
      },
      onError: (e) => notify.error(errorText(e, 'Save failed.')),
      onSaved: () => { onSaved(); void load(createdId ?? currentId); },
    });
  }, [draft, currentId, runSave, notify, onSaved, load]);

  const saveAs = async () => {
    // G5's confirmation first (DailyQCViewController.cs:1544-1611).
    if (!(await confirm({ title: 'Save As', message: 'Do you want to save as a new Template ID?' }))) return;
    const name = await prompt({
      title: 'Save As', message: 'Save this configuration as a new template named:',
      initialValue: `${draft.templateName.trim() || 'Template'} - Copy`.slice(0, MAX_TEMPLATE_NAME), required: true, confirmLabel: 'Save As',
    });
    if (name === null || !name.trim()) return;
    const copy: BuilderDraft = { ...draft, templateName: name.trim(), fields: draft.fields.map((f) => ({ ...f, oid: null })) };
    let createdId: string | null = null;
    await runSave({
      formName: FORM_NAME, auditAction: 'create',
      save: async () => { createdId = (await createTemplate(copy)).oid; },
      onError: (e) => notify.error(errorText(e, 'Save As failed.')),
      onSaved: () => { onSaved(); void load(createdId); },
    });
  };

  const leave = (go: () => void) => { void confirmDiscardIfUnsaved().then((ok) => { if (ok) { resetUnsavedTracking(); go(); } }); };

  const remove = async () => {
    if (!currentId) return;
    await runDelete({
      formName: FORM_NAME,
      message: `Delete the Daily QC template "${saved.templateName}"?`,
      delete: async () => { await deleteTemplate(currentId); },
      onError: (e) => notify.error(errorText(e, 'Delete failed.')),
      onDeleted: () => { resetUnsavedTracking(); onSaved(); onClose(); },
    });
  };

  const resetFields = () => set({ fields: saved.fields });

  useHotkeys([{ combo: 'mod+s', label: 'Save', group: 'Daily QC Log Book Builder', run: () => void save(), disabled: loading }]);

  // ── derived ──────────────────────────────────────────────────────────────

  const instrumentValue = draft.instrumentOid ?? (draft.instrumentCode ? CODE_ONLY : '');
  const instrumentName = draft.instrumentOid
    ? instruments.find((i) => i.id === draft.instrumentOid)?.name ?? draft.instrumentName ?? ''
    : draft.instrumentName ?? '';
  const methodValue = draft.methodOid ?? (draft.methodText ? TEXT_ONLY : '');
  const fieldsChanged = JSON.stringify(draft.fields) !== JSON.stringify(saved.fields);

  const steps = useMemo<IslandStep[]>(() => [
    { id: 'configure', label: 'Configure', state: tab === 'builder' ? 'active' : 'done' },
    { id: 'entry', label: 'Data Entry', state: tab === 'preview' ? 'active' : 'todo' },
    { id: 'review', label: 'Review', state: 'todo' },
  ], [tab]);

  const recordName = currentId ? (saved.templateName || '(no name)') : 'New';

  return (
    <div className="bl-page mlv-host qcb-page">
      <TopbarStepper steps={steps} />
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={() => leave(() => onHome?.())}>Home</span>
          <i className="ti ti-chevron-right" />
          {group && <><span>{group}</span><i className="ti ti-chevron-right" /></>}
          <span className="dp-bc-link" onClick={() => leave(onClose)}>{title}</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">{recordName}</span>
        </div>
        <FormTitle pageId="set-dailyqc-logbook" auditFormName={FORM_NAME} auditSourceId={currentId ?? undefined} loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area">
        <div className="qcb-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'builder'} className={`qcb-tab${tab === 'builder' ? ' active' : ''}`}
            onClick={() => setTab('builder')}>Daily QC Log Book Builder</button>
          <button type="button" role="tab" aria-selected={tab === 'preview'} className={`qcb-tab${tab === 'preview' ? ' active' : ''}`}
            onClick={() => setTab('preview')}>Logbook Preview</button>
        </div>

        {tab === 'preview' ? (
          <BuilderPreview fields={draft.fields} templateName={draft.templateName} />
        ) : (
          <>
            <div className="qcb-toolbar">
              {/* No ‹ Back button (owner request 2026-09-28): the breadcrumb returns to the list. */}
              <AppToolbar
                onNew={() => leave(() => { void load(null); })}
                onDelete={currentId ? () => void remove() : undefined}
                onRefresh={() => leave(() => { void load(currentId); })}
                refreshing={loading}
                beforeExport={(
                  <>
                    <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={loading}
                      title={`Save (${formatCombo('mod+s')})`}><span className="tb-i">💾</span> Save</button>
                    <button type="button" className="ca-btn" onClick={() => void saveAs()} disabled={loading}>Save As</button>
                  </>
                )}
                showMeta
              />
            </div>

            <section className="qcb-section">
              <div className="qcb-sectitle">Daily QC Log Book Builder</div>
              <div className="qcb-formgrid">
                <label className="qcb-label" htmlFor="qcb-tid">Template ID:</label>
                <input id="qcb-tid" className="qcb-in qcb-readonly" readOnly value={templateId ?? ''} placeholder="Autogenerated" />
                <label className="qcb-label" htmlFor="qcb-start">Start Date:</label>
                <input id="qcb-start" className="qcb-in" type="date" value={draft.startDate ?? ''}
                  onChange={(e) => set({ startDate: e.target.value || null })} />
                <label className="qcb-label" htmlFor="qcb-rec">Recurrence:</label>
                <div className="qcb-recwrap">
                  <input id="qcb-rec" className="qcb-in qcb-readonly" readOnly value={draft.recurrenceSummary || NO_RECURRENCE} />
                  <button type="button" className="ca-btn qcb-ellipsis" title="Set recurrence" aria-label="Set recurrence"
                    onClick={() => setRecurrenceOpen(true)}>…</button>
                </div>

                <label className="qcb-label qcb-req" htmlFor="qcb-name">Template Name:</label>
                <input id="qcb-name" className="qcb-in" maxLength={MAX_TEMPLATE_NAME} value={draft.templateName}
                  onChange={(e) => set({ templateName: e.target.value })} />
                <label className="qcb-label qcb-req" htmlFor="qcb-cat">Category:</label>
                <select id="qcb-cat" className="qcb-in" value={draft.categoryOid ?? ''} onChange={(e) => set({ categoryOid: e.target.value || null })}>
                  <option value="" />
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <span className="qcb-label">Status:</span>
                <div className="qcb-status" role="group" aria-label="Status">
                  <label><input type="radio" name="qcb-status" checked={draft.status === 'Active'} onChange={() => set({ status: 'Active' })} /> Active</label>
                  <label><input type="radio" name="qcb-status" checked={draft.status === 'Retired'} onChange={() => set({ status: 'Retired' })} /> Retired</label>
                  <label><input type="checkbox" checked={draft.notifyOnFailure} onChange={(e) => set({ notifyOnFailure: e.target.checked })} /> Notify on failure</label>
                </div>

                <label className="qcb-label" htmlFor="qcb-test">QC Check:</label>
                <select id="qcb-test" className="qcb-in" value={draft.testOid ?? ''}
                  onChange={(e) => set({ testOid: e.target.value || null, methodOid: null })}>
                  <option value="" />
                  {tests.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <label className="qcb-label" htmlFor="qcb-method">Method:</label>
                <select id="qcb-method" className="qcb-in" value={methodValue}
                  onChange={(e) => { if (e.target.value !== TEXT_ONLY) set({ methodOid: e.target.value || null }); }}>
                  <option value="" />
                  {methodValue === TEXT_ONLY && <option value={TEXT_ONLY}>{draft.methodText}</option>}
                  {methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <span /><span />

                <label className="qcb-label" htmlFor="qcb-inst">Instrument ID:</label>
                <select id="qcb-inst" className="qcb-in" value={instrumentValue}
                  onChange={(e) => { if (e.target.value !== CODE_ONLY) set({ instrumentOid: e.target.value || null }); }}>
                  <option value="" />
                  {instrumentValue === CODE_ONLY && <option value={CODE_ONLY}>{draft.instrumentCode}</option>}
                  {instruments.map((i) => <option key={i.id} value={i.id}>{i.code}</option>)}
                </select>
                <label className="qcb-label" htmlFor="qcb-iname">Instrument:</label>
                <input id="qcb-iname" className="qcb-in qcb-readonly" readOnly value={instrumentName} />
                <span /><span />

                <label className="qcb-label" htmlFor="qcb-instr">Instructions:</label>
                <input id="qcb-instr" className="qcb-in qcb-span5" maxLength={MAX_INSTRUCTIONS} value={draft.instructions ?? ''}
                  onChange={(e) => set({ instructions: e.target.value || null })} />
              </div>
            </section>

            <section className="qcb-section">
              <div className="qcb-sectitle">Field Configuration
                <span className="qcb-note">Give a field a Symbol to use it in formulas. Filled ƒx / ✓ means a formula is set — hover to see it.</span>
              </div>
              <BuilderFieldConfig fields={draft.fields} available={available} readOnly={false}
                onChange={setFields} onOpenFormula={(index, kind) => setFormula({ index, kind })} />
              <div className="qcb-footer-actions">
                <button type="button" className="ca-btn" disabled={!fieldsChanged} onClick={resetFields}
                  title="Undo the unsaved field changes">Reset</button>
                <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={loading}>Save Configuration</button>
              </div>
            </section>
          </>
        )}
      </div>

      {formula && (
        <BuilderFormulaDialog fields={draft.fields} index={formula.index} kind={formula.kind}
          onCancel={() => setFormula(null)}
          onApply={(text) => {
            setFields(draft.fields.map((f, i) => (i === formula.index ? { ...f, [formula.kind]: text || null } : f)));
            setFormula(null);
          }} />
      )}
      {recurrenceOpen && (
        <BuilderRecurrenceDialog value={draft.recurrence} startDate={draft.startDate}
          onCancel={() => setRecurrenceOpen(false)}
          onApply={(r, summary) => { set({ recurrence: r, recurrenceSummary: r ? summary : NO_RECURRENCE }); setRecurrenceOpen(false); }} />
      )}
    </div>
  );
}
